import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';

type Club = {
  id: string;
  slug: string;
  name: string;
  location?: string | null;
  description?: string | null;
  hero_image?: string | null;
  logo?: string | null;
  instagram_handle?: string | null;
};

type Run = {
  id: string;
  club_id: string;
  title?: string | null;
  day_of_week: string;
  start_time: string;
  location_address?: string | null;
  distance?: string | null;
  vibes: string[];
  notes?: string | null;
};

type TodayClub = Club & {
  todayRuns: Run[];
  goingCount: number;
  interestedCount: number;
  primaryRunId: string;
};

function getTodayDayName() {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(
    new Date()
  );
}

function getIsoToday() {
  return new Date().toISOString().slice(0, 10);
}

function shuffleArray<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function formatTime(time?: string | null) {
  if (!time) return 'Time TBD';

  const [rawHour, rawMinute] = time.split(':');
  const hour = Number(rawHour);
  const minute = (rawMinute ?? '00').split(' ')[0];

  if (Number.isNaN(hour)) return time;

  const normalizedHour = hour % 12 || 12;
  const suffix = hour >= 12 ? 'PM' : 'AM';

  return `${normalizedHour}:${minute} ${suffix}`;
}

function firstLineAddress(address?: string | null) {
  if (!address) return 'Location TBD';
  const parts = address.split(',').map((p) => p.trim());
  const street = parts[0];
  const city = parts[1] ?? null;
  const zipMatch = address.match(/\b\d{5}(-\d{4})?\b/);
  const zip = zipMatch ? zipMatch[0] : null;
  if (!city) return street;
  if (zip) return `${street}, ${city}, ${zip}`;
  return `${street}, ${city}`;
}

export default function Home() {
  const [allClubs, setAllClubs] = useState<Club[]>([]);
  const [todayClubs, setTodayClubs] = useState<TodayClub[]>([]);
  const [loading, setLoading] = useState(true);

  const todayDayName = useMemo(() => getTodayDayName(), []);

  const fetchHomeData = useCallback(async () => {
    try {
      setLoading(true);

      const isoToday = getIsoToday();
      const runFields = 'id, club_id, title, day_of_week, start_time, location_address, distance, vibes, notes';

      const [clubsResult, recurringResult, oneOffResult] = await Promise.all([
        supabase
          .from('clubs')
          .select(
            'id, slug, name, location, description, hero_image, logo, instagram_handle'
          )
          .order('created_at', { ascending: false })
          .limit(24),

        // Recurring runs for today's day name
        supabase
          .from('runs')
          .select(runFields)
          .eq('day_of_week', todayDayName)
          .eq('is_recurring', true)
          .eq('is_active', true),

        // One-off runs for today's exact date
        supabase
          .from('runs')
          .select(runFields)
          .eq('run_date', isoToday)
          .eq('is_recurring', false)
          .eq('is_active', true),
      ]);

      if (clubsResult.error) throw clubsResult.error;
      if (recurringResult.error) throw recurringResult.error;
      if (oneOffResult.error) throw oneOffResult.error;

      const clubsData = clubsResult.data ?? [];
      const runsData = [...(recurringResult.data ?? []), ...(oneOffResult.data ?? [])];

      setAllClubs(clubsData);

      if (runsData.length === 0) {
        setTodayClubs([]);
        return;
      }

      const uniqueClubIds = [...new Set(runsData.map((run) => run.club_id))];

      const { data: todayClubData, error: todayClubError } = await supabase
        .from('clubs')
        .select(
          'id, slug, name, location, description, hero_image, logo, instagram_handle'
        )
        .in('id', uniqueClubIds);

      if (todayClubError) throw todayClubError;

      const runsByClubId = new Map<string, Run[]>();

      for (const run of runsData) {
        const existing = runsByClubId.get(run.club_id) ?? [];
        existing.push(run);
        runsByClubId.set(run.club_id, existing);
      }

      // Fetch RSVP counts for all today's runs
      const allRunIds = runsData.map((r) => r.id);
      const { data: rsvpCountData } = await supabase
        .from('run_rsvps')
        .select('run_id, status')
        .in('run_id', allRunIds)
        .eq('run_date', isoToday)
        .in('status', ['going', 'interested']);

      // Build count maps: runId → { going, interested }
      const rsvpCounts = new Map<string, { going: number; interested: number }>();
      for (const row of rsvpCountData ?? []) {
        const cur = rsvpCounts.get(row.run_id) ?? { going: 0, interested: 0 };
        if (row.status === 'going') cur.going += 1;
        if (row.status === 'interested') cur.interested += 1;
        rsvpCounts.set(row.run_id, cur);
      }

      const combined: TodayClub[] = (todayClubData ?? []).map((club) => {
        const runs = (runsByClubId.get(club.id) ?? []).sort((a, b) =>
          a.start_time.localeCompare(b.start_time)
        );
        const primaryRun = runs[0];
        const counts = primaryRun ? (rsvpCounts.get(primaryRun.id) ?? { going: 0, interested: 0 }) : { going: 0, interested: 0 };
        return {
          ...club,
          todayRuns: runs,
          goingCount: counts.going,
          interestedCount: counts.interested,
          primaryRunId: primaryRun?.id ?? '',
        };
      });

      combined.sort((a, b) => {
        const aTime = a.todayRuns[0]?.start_time ?? '';
        const bTime = b.todayRuns[0]?.start_time ?? '';
        return aTime.localeCompare(bTime);
      });

      setTodayClubs(combined);
    } catch (error) {
      console.error('Error fetching home data:', error);
      setAllClubs([]);
      setTodayClubs([]);
    } finally {
      setLoading(false);
    }
  }, [todayDayName]);

  useEffect(() => {
    fetchHomeData();
  }, [fetchHomeData]);

  const todayClubIds = useMemo(
    () => new Set(todayClubs.map((club) => club.id)),
    [todayClubs]
  );

  const heroImageClubs = useMemo(() => {
    const eligible = allClubs.filter(
      (club) => club.hero_image && !todayClubIds.has(club.id)
    );
    return shuffleArray(eligible).slice(0, 8);
  }, [allClubs, todayClubIds]);

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] text-black">
      <section className="px-4 pb-8 pt-4 sm:px-6 md:pb-12 md:pt-6">
        <div className="mx-auto max-w-7xl">
          <div className="relative overflow-hidden rounded-[2rem] border border-black/10 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
            <div className="grid h-[500px] grid-cols-2 gap-1 bg-black/5 sm:h-[580px] sm:grid-cols-4">
              {loading
                ? Array.from({ length: 8 }).map((_, index) => (
                    <div
                      key={index}
                      className="animate-pulse bg-gradient-to-br from-[#fff7cc] via-[#f8f8f8] to-[#f6e8fb]"
                    />
                  ))
                : heroImageClubs.length > 0
                ? heroImageClubs.map((club, index) => (
                    <div
                      key={club.id}
                      className="relative overflow-hidden"
                      style={{
                        backgroundColor:
                          index % 2 === 0 ? '#f6e8fb' : '#fff7cc',
                      }}
                    >
                      <img
                        src={club.hero_image ?? ''}
                        alt={club.name}
                        className="h-full w-full object-cover opacity-80"
                        style={{ objectPosition: 'center 35%' }}
                      />
                    </div>
                  ))
                : Array.from({ length: 8 }).map((_, index) => (
                    <div
                      key={index}
                      style={{
                        background:
                          index % 2 === 0
                            ? 'linear-gradient(135deg, #f6e8fb 0%, #ffffff 100%)'
                            : 'linear-gradient(135deg, #fff7cc 0%, #ffffff 100%)',
                      }}
                    />
                  ))}
            </div>

            <div className="absolute inset-0 bg-white/20" />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(135deg, rgba(255,247,204,0.28) 0%, rgba(255,255,255,0.08) 45%, rgba(246,232,251,0.30) 100%)',
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/10 to-white/10" />

            <div className="absolute inset-0 flex items-center justify-center px-6">
              <div className="flex max-w-3xl flex-col items-center gap-1 text-center sm:gap-2">
                <img
                  src="/images/runcollogo.png"
                  alt="RunCollective"
                  className="h-48 w-auto object-contain sm:h-56 md:h-64 lg:h-72"
                  style={{
                    filter:
                      'drop-shadow(0 8px 24px rgba(0,0,0,0.35)) drop-shadow(0 0 18px rgba(255,255,255,0.22))',
                  }}
                />

                <div className="-mt-1 sm:-mt-2">
                  <Link
                    to="/clubs"
                    className="inline-flex items-center justify-center rounded-2xl bg-[#ffde59] px-7 py-3.5 text-xl font-bangers tracking-wide text-[#e2a9f1] shadow-[0_10px_30px_rgba(0,0,0,0.16)] transition hover:scale-[1.02]"
                  >
                    FIND YOUR PEOPLE
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 md:py-12">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-black/45">
              Today
            </p>
            <h2 className="mt-1 text-2xl font-black tracking-tight text-black sm:text-3xl">
              {todayDayName}&rsquo;s runs
            </h2>
          </div>

          <Link
            to="/today"
            className="hidden rounded-2xl border border-black/10 bg-white/80 px-4 py-2 text-sm font-bold text-black transition hover:bg-[#fff7cc] sm:inline-flex"
          >
            View today&apos;s runs
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-3xl border border-black/10 bg-white/80 shadow-sm"
              >
                <div className="h-52 animate-pulse bg-gradient-to-br from-[#fff7cc] via-[#f8f8f8] to-[#f6e8fb]" />
                <div className="space-y-3 p-4">
                  <div className="h-4 w-2/3 animate-pulse rounded bg-black/10" />
                  <div className="h-3 w-1/3 animate-pulse rounded bg-black/10" />
                  <div className="h-3 w-full animate-pulse rounded bg-black/5" />
                </div>
              </div>
            ))}
          </div>
        ) : todayClubs.length === 0 ? (
          <div className="rounded-3xl border border-black/10 bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] px-5 py-12 text-center shadow-sm">
            <h3 className="text-lg font-black text-black">
              No runs scheduled today
            </h3>
            <p className="mt-2 text-sm text-black/70">
              Add a run and it will show up here.
            </p>
            <Link
              to="/today"
              className="mt-5 inline-flex rounded-2xl bg-black px-5 py-3 text-sm font-bold text-white transition hover:opacity-90"
            >
              View today&apos;s runs
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {todayClubs.map((club) => {
              const primaryRun = club.todayRuns[0];
              const extraRuns = Math.max(club.todayRuns.length - 1, 0);
              const hasRsvps = club.goingCount > 0 || club.interestedCount > 0;

              return (
                <Link
                  key={club.id}
                  to={`/clubs/${club.slug}`}
                  className="group overflow-hidden rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] shadow-sm transition hover:-translate-y-0.5"
                >
                  <div className="relative h-52 overflow-hidden bg-zinc-100">
                    {club.hero_image ? (
                      <img
                        src={club.hero_image}
                        alt={club.name}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                        style={{ objectPosition: 'center 33%' }}
                      />
                    ) : (
                      <div
                        className="h-full w-full"
                        style={{
                          background:
                            'linear-gradient(135deg, #fff7cc 0%, #ffffff 45%, #f6e8fb 100%)',
                        }}
                      />
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/18 to-transparent" />

                    {club.logo ? (
                      <div className="absolute left-4 top-4 h-14 w-14 overflow-hidden rounded-2xl border border-white/50 bg-white shadow-md">
                        <img
                          src={club.logo}
                          alt={club.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : null}

                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="text-xl font-black tracking-tight">
                        {club.name}
                      </h3>
                      <p className="mt-1 text-sm text-white/85">
                        {firstLineAddress(
                          primaryRun?.location_address || club.location
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.14em] text-black/45">
                          {primaryRun?.title || 'Next up'}
                        </p>
                        <p className="mt-1 text-base font-black text-black">
                          {formatTime(primaryRun?.start_time)}
                        </p>
                      </div>

                      {extraRuns > 0 ? (
                        <span className="rounded-full border border-black/10 bg-[#fff7cc] px-3 py-1 text-xs font-bold text-black/60">
                          +{extraRuns} more
                        </span>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {primaryRun?.distance ? (
                        <span className="rounded-full border border-black/10 bg-white px-3 py-1 text-xs font-medium text-black/65">
                          {primaryRun.distance}
                        </span>
                      ) : null}

                      {(primaryRun?.vibes || []).slice(0, 2).map((vibe) => (
                        <span
                          key={vibe}
                          className="rounded-full border border-black/10 bg-white px-3 py-1 text-xs font-medium text-black/65"
                        >
                          {vibe}
                        </span>
                      ))}
                    </div>

                    <p className="text-sm text-black/60">
                      {club.description
                        ? club.description
                        : 'Tap to view the club profile and full schedule.'}
                    </p>

                    {hasRsvps && club.primaryRunId && (
                      <div
                        className="pt-1"
                        onClick={(e) => e.preventDefault()}
                      >
                        <Link
                          to={`/clubs/${club.slug}/runs/${club.primaryRunId}/attending?date=${new Date().toISOString().slice(0, 10)}`}
                          className="text-xs font-medium text-black/50 underline-offset-2 hover:text-black hover:underline"
                        >
                          {club.goingCount > 0 && `${club.goingCount} going`}
                          {club.goingCount > 0 && club.interestedCount > 0 && ' · '}
                          {club.interestedCount > 0 && `${club.interestedCount} interested`}
                        </Link>
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}